/*
* Created on Jan 21, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import React, {useState, useEffect} from 'react';
import ic_content_site_svg from '../../static/img/content/ic-content-site.svg';
import TableComponent from "../common/TableComponent/TableComponent";
import SiteTableRowComponent from "./SiteTableRowComponent";
import {useOutletContext, useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import SiteCreateModalComponent from "./SiteCreateModalComponent"
import ModalAlertComponent from "../common/ModalComonent/ModalAlertComponent";
import {useAuthSession} from "../../context/AuthSessionContext";
import {useTranslation} from "react-i18next";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useReducerAsync} from "use-reducer-async";
import {useOrganization} from "../../context/OrganizationContext";
import useEmailChangedHook from "../../hook/EmailChangedHook";
import ModalErrorComponent from "../common/ModalComonent/ModalErrorComponent";
import SiteAdminStandaloneModalComponent from "./SiteAdminStandaloneModalComponent";


const table_ui = {
    toolbar: {
        count: true,
        create_button: true,
        delete_button: true,
        export_button: false
    },
    table_field_checkbox: true
}

const SiteIndexComponent = () => {
    const { userPermissions} = useOutletContext();
    const {userProfile} = useOrganization();
    const {is_owner} = userProfile();
    const {t, i18n} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const {useCheckSession} = useAuthSession();
    const {oid} = useParams();
    const [page, setPage] = useState({limit:10, offset:0})
    const [refresh, setRefresh] = useState(0)
    const {data:org_sites, isSuccess} = useQuery({
        queryKey: [`organization-center/${oid}/site`, page, refresh, i18n.language],
        queryFn: async ({signal}) => {
            const org_user_list = await OrganizationAPI.get(`organization-center/${oid}/site`, {...page, gt: true, lang: i18n.language}, false, signal);
            return org_user_list.result;
        }
    });
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertConfirmAction, setAlertConfirmAction] = useState({});
    const [rowData, setRowData] = useState(null);
    const [modalMode, setModalMode] = useState('create');
    const {EmailWarningDialog} = useEmailChangedHook();
    const [noopSites, setNoopSites] = useState([]);

    const closeCreateModal = (()=>{
        setOpenCreateModal(false);
    });

    // Non-owners (member, user_manager) see a strict subset per TEM61510-22480
    // spec: site_name, description, created_at. The role-aware site endpoint
    // doesn't ship admin info to them, and they can't perform any actions, so
    // those columns are hidden entirely (not just their cells emptied).
    const all_table_head_fields = [
        {field_id: 'name', field_name: t('@Site Name@'), isSortable: true, width_style: '!w-20', default_sort: true, default_sort_order: ''},
        {field_id: 'description', field_name: t('@Description@'), isSortable: false, width_style: '!w-30'},
        {field_id: 'administrator', field_name: t('@Administrator@'), isSortable: false, width_style: '!w-25'},
        {field_id: 'created_at', field_name: t('@Creation date@'), isSortable: true, width_style: '!w-15', default_sort: false, default_sort_order: '-'},
        {field_id: 'action', field_name: t('@Action@'), isSortable: false, width_style: '!w-10'},
    ];
    const table_head_fields = is_owner
        ? all_table_head_fields
        : all_table_head_fields.filter(f => f.field_id !== 'administrator' && f.field_id !== 'action');

    const {data: groupMap, isFetched:isFetchedGroup} = useQuery({
        queryKey: [`organization/${oid}/group`],
        queryFn: async () => {
            const group_list = await OrganizationAPI.get(`organization/${oid}/group`, {limit: 200});
            let groupMap = {};
            group_list.result.forEach(g => {
                groupMap[g.organization_group_id] = g;
            });
            return groupMap;
        }
    });

    const {data:userMap, isFetched:isFetchedUser} = useQuery({
        queryKey: [`organization/${oid}/user`],
        queryFn: async () => {
            const user_list = await OrganizationAPI.get(`organization/${oid}/user`, {limit: 200});
            let userMap = {};
            user_list.result.forEach(u => {
                u.name = u.display_name;
                userMap[u.id] = u;
            });
            return userMap;
        }
    });

    const dispatch_handler = (site_state, action) => {
        switch (action.type) {
            case 'init':
                site_state.data = action.payload.sites;
                site_state.total_record = action.payload.total;
                site_state.select_box = false;
                site_state.search = (obj, query) => {
                    return obj.name.match(new RegExp(query, 'i'))
                };
                site_state.group_map = groupMap || {};
                site_state.user_map = userMap || {};
                site_state.showStandaloneAdmin = false;
                return {...site_state};
            case 'pagination':
                setPage({limit:action.payload.limit, offset:action.payload.offset});
                if (action.payload.refresh) {
                    setRefresh(prev=>prev+1);
                }
                return {...site_state}
            case 'refresh':
                setRefresh((prevVal)=>prevVal+1);
                return site_state;
            case 'search_sites':
                return {
                    ...site_state,
                    query: action.payload
                };
            case 'create_sites':
                setModalMode('create');
                setRowData(null);
                setOpenCreateModal(true);
                return site_state;
            case 'open_alert_delete_sites':
                setOpenAlert(true);
                setAlertConfirmAction('delete_sites');
                return site_state;
            case 'select_all':
                return {
                    ...site_state,
                    data: site_state.data.map((g)=>({...g, checked: !g.default})),
                    select_box: true
                }
            case 'select_none':
                return {
                    ...site_state,
                    data: site_state.data.map((g)=>({...g, checked: false})),
                    select_box: false
                }
            case 'toggle_checkbox':
                const d = site_state.data.map((o) => {
                    if (action.payload.organization_site_id === o.organization_site_id) {
                        return {...o, checked: !o.checked}
                    }
                    return o;
                });
                let check_status = d?.every((o) => o.checked);
                if (!check_status) {
                    check_status = d?.some((o) => o.checked) ? 'partial' : false;
                }
                return {
                    ...site_state,
                    data: d,
                    select_box: check_status
                }
            case 'open_edit_modal':
                setModalMode('edit');
                setRowData(action.payload);
                setOpenCreateModal(true);
                return site_state;
            case 'update_group_map':
                return {
                    ...site_state,
                    group_map: action.payload
                }
            case 'update_user_map':
                return {
                    ...site_state,
                    user_map: action.payload
                }
            case 'toggle_standalone_admin':
                return {
                    ...site_state,
                    showStandaloneAdmin: action.payload.show_admin,
                    row_data: action.payload.row_data || null
                }

            // case 'delete_sites':
            //     const doDeleteSite = async () => {
            //         setOpenAlert(false);
            //         const sites_to_delete = site_state.data.filter((o)=>o.checked).map((o)=>o.organization_site_id);
            //         await OrganizationAPI.delete(`organization/${oid}/site`, null, {organization_site_ids: sites_to_delete})
            //         console.log(sites_to_delete);
            //         setRefresh((prevVal)=>prevVal+1);
            //         return {
            //             ...site_state,
            //             data: site_state.data.filter((o,i)=>i!==0)
            //         }
            //     }
            //     doDeleteSite();
            default:
                return site_state;
        }
    }

    const asyncDispatchHandler = {
        delete_sites: async ({dispatch, getState}) => {
            const cur_state = getState();
            const sites_to_delete = cur_state.data.filter((o)=>o.checked).map((o)=>o.organization_site_id);
            try {
                await OrganizationAPI.delete(`organization/${oid}/site`, null, {organization_site_ids: sites_to_delete})
                setOpenAlert(false);
                setRefresh((prevVal)=>prevVal+1);
            } catch (e) {
                if (e?.response?.data?.code === 1060026) {
                    setOpenAlert(false);
                    setNoopSites(e.response.data.result.untouched_sites || []);
                    setRefresh((prevVal)=>prevVal+1);
                }
                else {
                    throw e;
                }
            }
        }
    }

    // const [state, dispatch] = useReducer(dispatch_handler, {});
    const [state, dispatch] = useReducerAsync(dispatch_handler, {
        data: org_sites?.sites || [],
        total_record: org_sites?.total || 0,
        select_box: false,
        group_map: {},
        user_map: {},
        userPermissions,
        search: (obj, query) => {return obj.name.match(new RegExp(query, 'i'))}
    }, asyncDispatchHandler);
    useEffect(()=> {
        if (org_sites && isFetchedGroup && isFetchedUser) {
            dispatch({type: 'init', payload: org_sites});
        }
    }, [org_sites, refresh, isFetchedGroup, isFetchedUser]);

    useEffect(() => {
        if (groupMap) {
            dispatch({type: 'update_group_map', payload: groupMap});
        }
    }, [groupMap]);

    useEffect(() => {
        if (userMap) {
            dispatch({type: 'update_user_map', payload: userMap});
        }
    }, [userMap]);

    useCheckSession();

    return (
        <>
            <div className="content content-page">
                <TableComponent
                    name="Sites"
                    table_icon={ic_content_site_svg}
                    table_head_fields={table_head_fields}
                    table_data={state}
                    state={state}
                    total_record={state.total_record}
                    search={true}
                    disPatcher={dispatch}
                    row_component={SiteTableRowComponent}
                    table_ui={table_ui}
                    isOwner={is_owner}
                    data_table_name="sites-table"
                >
                </TableComponent>
            </div>
            {openCreateModal && <SiteCreateModalComponent openModal={openCreateModal} closeModal={closeCreateModal} doRefresh={setRefresh} organizationId={oid} initData={rowData} mode={modalMode}/>}
            {openAlert && <ModalAlertComponent isOpen={openAlert} onClose={setOpenAlert} onConfirmAction={ async ()=> await asyncDispatchHandler.delete_sites({dispatch, getState:()=>state})}>
                <div className="alert-msg-inner-head">{t('@Delete the selected Site(s)?@')}</div>
                <div>
                    {t('@Delete site msg@')}
                </div>
            </ModalAlertComponent>}

            {noopSites?.length > 0 && <ModalErrorComponent isOpen={noopSites?.length > 0} onClose={()=>setNoopSites([])} error_title={t("@Deleting Site Containing QNAP Devices@")}>
                <div>
                    <p>
                        {t("@The following sites contain assigned QNAP devices and cannot be deleted:@")}
                    </p>

                    <p>
                    {
                        noopSites?.map(item => <li style={{marginLeft: '2rem'}}>{item}</li>)
                    }
                    </p>

                    <p>
                        {t('@Please reassign the devices to another site before attempting deletion.@\n')}
                    </p>
                </div>

            </ModalErrorComponent>}

            {isSuccess && <EmailWarningDialog />}
            {state?.showStandaloneAdmin && <SiteAdminStandaloneModalComponent isOpen={state?.showStandaloneAdmin} t={t} dispatch={dispatch} state={state}/>}
        </>
    );
};

export default SiteIndexComponent;